interface SortOption {
  sort: string;
  order: string;
  label: string;
  icon: string;
}

export interface DropdownData {
  /** 所有可选排序项 */
  options: SortOption[];
  /** 服务端渲染时的初始选中项 */
  current: { sort: string; order: string };
}

export class Dropdown {
  private isOpen = false;
  private options: SortOption[];
  private toggle!: HTMLButtonElement;
  private backdrop!: HTMLDivElement;
  private label!: HTMLSpanElement;
  private icon!: Element;

  constructor(
    private container: HTMLElement,
    data: DropdownData,
  ) {
    this.options = data.options;
    this.toggle = container.querySelector(".toggle")!;
    this.backdrop = container.querySelector(".backdrop")!;
    this.menu = container.querySelector(".menu")!;
    this.chevron = container.querySelector(".chevron")!;
    this.label = container.querySelector(".label")!;
    this.icon =
      container.querySelector(".icon use") || container.querySelector(".icon")!;
    container.querySelectorAll("[data-option]").forEach((link) => {
      link.addEventListener("click", this.handleOptionClick);
    });
    this.toggle.addEventListener("click", this.handleToggleClick);
    this.backdrop.addEventListener("click", this.handleBackdropClick);
    document.addEventListener("keydown", this.handleEscapeKey);
  }

  private open(): void {
    const { menu, backdrop, chevron } = this;

    backdrop.classList.remove("hidden");
    menu.classList.remove("hidden");

    // 使用 requestAnimationFrame 触发过渡动画，避免强制回流 (forced reflow)
    requestAnimationFrame(() => {
      menu.classList.remove("opacity-0", "scale-95", "-translate-y-2");
      menu.classList.add("opacity-100", "scale-100", "translate-y-0");
      chevron.classList.add("rotate-180");
    });
  }

  private close(): void {
    const { menu, backdrop, chevron } = this;

    menu.classList.remove("opacity-100", "scale-100", "translate-y-0");
    menu.classList.add("opacity-0", "scale-95", "-translate-y-2");
    chevron.classList.remove("rotate-180");

    // 等动画结束后隐藏，时长与 CSS transition 保持一致（200ms）
    setTimeout(() => {
      if (!this.isOpen) {
        menu.classList.add("hidden");
        backdrop.classList.add("hidden");
      }
    }, 200);
  }

  private handleOptionClick = (e: Event): void => {
    const target = e.currentTarget as HTMLElement;
    const idx = parseInt(target.dataset.option!, 10)!;
    this.label.textContent = this.options[idx].label;
    this.icon.setAttribute("href", this.options[idx].icon);

    // 更新菜单中各选项的 check 图标
    this.container.querySelectorAll(".check").forEach((check, i) => {
      check.classList.toggle("hidden", i !== idx);
    });
    this.isOpen = false;
    this.close();
  };
  private handleToggleClick = (e: Event): void => {
    e.stopPropagation();
    this.isOpen = !this.isOpen;
    this.isOpen ? this.open() : this.close();
  };

  private handleBackdropClick = (): void => {
    this.isOpen = false;
    this.close();
  };

  private handleEscapeKey = (e: KeyboardEvent): void => {
    if (this.isOpen && e.key === "Escape") {
      this.isOpen = false;
      this.close();
    }
  };

  /**
   * Unpoly compiler 在元素被移除时自动调用
   */
  destroy(): void {
    this.toggle.removeEventListener("click", this.handleToggleClick);
    this.backdrop.removeEventListener("click", this.handleBackdropClick);
    document.removeEventListener("keydown", this.handleEscapeKey);
  }

  /**
   * 注册编译器
   * @param selector 选择器，默认为 '.dropdown'
   */
  static compiler(selector: string) {
    up.compiler(selector, (element: Element, data: DropdownData) => {
      const dropdown = new Dropdown(element as HTMLElement, data);
      return () => dropdown.destroy();
    });
  }
}
